import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { Link, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  working: '#10B981', busy: '#F59E0B', broken: '#EF4444', unknown: '#475569',
};
const CONNECTOR_TYPES = ['All','CCS','CHAdeMO','Type2','Tesla'];

function AutoLoader({ onLoad }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => onLoad(map), 300);
    return () => clearTimeout(t);
  }, []);
  useMapEvents({ moveend: (e) => onLoad(e.target) });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 14, { duration: 1.2 });
  }, [target]);
  return null;
}

export default function MapPage() {
  const [chargers, setChargers]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filter, setFilter]           = useState('all');
  const [connector, setConnector]     = useState('All');
  const [reportingId, setReportingId] = useState(null);
  const [reportStatus, setReportStatus] = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [stats, setStats]             = useState({ total: 0, working: 0, broken: 0 });
  const [search, setSearch]           = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch]   = useState(false);
  const [flyTarget, setFlyTarget]     = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const searchTimer = useRef(null);
  const loggedIn = !!localStorage.getItem('evapp_token');
  const navigate = useNavigate();

  const loadChargers = useCallback(async (map) => {
    setLoading(true);
    try {
      const b = map.getBounds();
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (zoom < 5) { setChargers([]); setLoading(false); return; }
      const latDelta = b.getNorth() - b.getSouth();
      const r = Math.min(Math.ceil(latDelta * 55), 150);
      const params = { lat: center.lat.toFixed(6), lng: center.lng.toFixed(6), radius: r, limit: 400 };
      if (filter !== 'all') params.status = filter;
      if (connector !== 'All') params.connector = connector;
      const res = await api.get('/chargers', { params });
      const data = res.data.chargers || [];
      setChargers(data);
      setStats({ total: data.length, working: data.filter(c => c.status === 'working').length, broken: data.filter(c => c.status === 'broken').length });
    } catch (_) {} finally { setLoading(false); }
  }, [filter, connector]);

  const handleSearch = (q) => {
    setSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/chargers/search', { params: { q } });
        setSearchResults(res.data.chargers || []);
      } catch (_) {}
    }, 400);
  };

  const goToResult = (c) => {
    setFlyTarget({ lat: Number(c.lat), lng: Number(c.lng) });
    setSearch(''); setSearchResults([]); setShowSearch(false);
  };

  const openNav = (c) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const submitReport = async (chargerId) => {
    if (!loggedIn) { navigate('/login?next=/'); return; }
    if (!reportStatus) { toast.error('Select a status first'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/reports', { charger_id: chargerId, status: reportStatus });
      toast.success(res.data.points_earned ? `+${res.data.points_earned} points earned!` : 'Report submitted!');
      setReportingId(null); setReportStatus('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const activeFilters = (filter !== 'all' ? 1 : 0) + (connector !== 'All' ? 1 : 0);

  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative', fontFamily: 'Inter, sans-serif', paddingTop: 56 }}>

      {/* Search bar */}
      <div style={{
        position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, width: '90%', maxWidth: 520,
        display: 'flex', gap: 8,
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <div
            onClick={() => setShowSearch(true)}
            style={{
              background: 'rgba(255,255,255,0.97)', border: '1px solid var(--border-2)',
              borderRadius: 12, padding: '10px 14px', cursor: 'text',
              display: 'flex', alignItems: 'center', gap: 8,
              backdropFilter: 'blur(16px)',
            }}
          >
            <span style={{ fontSize: 14 }}>🔍</span>
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>Search chargers, cities…</span>
            {loading && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--teal)' }}>Loading…</span>}
          </div>

          {/* Search dropdown */}
          {showSearch && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              background: 'var(--slate)', borderRadius: 12,
              border: '1px solid var(--border-2)', overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border)', gap: 8 }}>
                <span style={{ fontSize: 14 }}>🔍</span>
                <input
                  autoFocus
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Search chargers, cities…"
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-1)', fontSize: 13 }}
                />
                <button onClick={() => { setShowSearch(false); setSearch(''); setSearchResults([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>
              {searchResults.map(c => (
                <div key={c.id} onClick={() => goToResult(c)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--navy)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[c.status] || STATUS_COLORS.unknown, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.city}, {c.country}</div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--teal)', flexShrink: 0 }}>Go →</span>
                </div>
              ))}
              {search.length >= 2 && searchResults.length === 0 && (
                <div style={{ padding: '14px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No chargers found</div>
              )}
            </div>
          )}
        </div>

        {/* Filter button */}
        <button onClick={() => setShowFilters(f => !f)} style={{
          width: 44, height: 44, borderRadius: 12, border: `1px solid ${activeFilters > 0 ? 'var(--teal)' : 'var(--border-2)'}`,
          background: activeFilters > 0 ? 'var(--teal-dim)' : 'rgba(255,255,255,0.97)',
          cursor: 'pointer', position: 'relative', backdropFilter: 'blur(16px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
        }}>⚙️
          {activeFilters > 0 && (
            <div style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--navy)' }}>{activeFilters}</span>
            </div>
          )}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div style={{
          position: 'absolute', top: 120, right: 16, zIndex: 1000,
          background: 'var(--slate)', border: '1px solid var(--border-2)',
          borderRadius: 14, padding: 16, width: 240,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(16px)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Filters</div>

          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Status</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {['all','working','busy','broken'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '4px 10px', borderRadius: 10, border: `1px solid ${filter === f ? (STATUS_COLORS[f] || 'var(--teal)') : 'var(--border-2)'}`,
                background: filter === f ? (STATUS_COLORS[f] || 'var(--teal)') + '22' : 'var(--navy)',
                color: filter === f ? (STATUS_COLORS[f] || 'var(--teal)') : 'var(--text-2)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}>{f === 'all' ? 'All' : f}</button>
            ))}
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Connector</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {CONNECTOR_TYPES.map(ct => (
              <button key={ct} onClick={() => setConnector(ct)} style={{
                padding: '4px 10px', borderRadius: 10, border: `1px solid ${connector === ct ? 'var(--teal)' : 'var(--border-2)'}`,
                background: connector === ct ? 'var(--teal-dim)' : 'var(--navy)',
                color: connector === ct ? 'var(--teal)' : 'var(--text-2)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>{ct}</button>
            ))}
          </div>

          <button onClick={() => { setFilter('all'); setConnector('All'); }} style={{
            width: '100%', padding: '7px 0', borderRadius: 8, border: '1px solid #EF444444',
            background: 'none', color: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Clear All</button>
        </div>
      )}

      {/* Stats pill */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, background: 'rgba(255,255,255,0.95)', borderRadius: 20,
        padding: '8px 16px', border: '1px solid var(--border-2)',
        display: 'flex', gap: 10, alignItems: 'center', backdropFilter: 'blur(16px)',
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{stats.total} chargers</span>
        <span style={{ fontSize: 12, color: '#10B981' }}>● {stats.working} working</span>
        <span style={{ fontSize: 12, color: '#EF4444' }}>● {stats.broken} broken</span>
      </div>

      {/* Map */}
      <MapContainer
        center={[51.5, 10.0]} zoom={6}
        style={{ height: '100%', width: '100%', background: '#F0F4FF' }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        />
        <AutoLoader onLoad={loadChargers} />
        {flyTarget && <FlyTo target={flyTarget} />}

        {chargers.map(c => (
          <CircleMarker
            key={c.id}
            center={[Number(c.lat), Number(c.lng)]}
            radius={filter === 'all' ? 6 : 8}
            pathOptions={{
              color: STATUS_COLORS[c.status] || STATUS_COLORS.unknown,
              fillColor: STATUS_COLORS[c.status] || STATUS_COLORS.unknown,
              fillOpacity: 0.9, weight: 1.5,
            }}
          >
            <Popup minWidth={240} maxWidth={300}>
              <div style={{ fontFamily: 'Inter, sans-serif', background: 'var(--slate)', margin: -14, borderRadius: 12, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-1)', lineHeight: 1.3 }}>{c.name}</div>
                    <StatusBadge status={c.status} size="sm" />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    📍 {[c.city, c.country].filter(Boolean).join(', ')}
                    {c.power_kw ? ` · ${c.power_kw}kW` : ''}
                  </div>
                  {c.connector_types?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--teal)', marginTop: 4, fontWeight: 600 }}>{c.connector_types.join(' · ')}</div>
                  )}
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 14px', background: 'var(--navy)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { label: 'Reliability', value: `${c.reliability_score}/10`, color: 'var(--teal)' },
                    { label: 'Reports', value: c.report_count || 0 },
                    { label: 'Power', value: c.power_kw ? `${c.power_kw}kW` : '—' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: color || 'var(--text-1)' }}>{value}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <button onClick={() => openNav(c)} style={{
                    padding: '8px 0', borderRadius: 8, border: 'none',
                    background: 'var(--teal)', color: 'var(--navy)',
                    fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}>🧭 Navigate</button>
                  <Link to={`/charger/${c.slug}`} style={{
                    padding: '8px 0', borderRadius: 8, border: '1px solid var(--border-2)',
                    color: 'var(--teal)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                    textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}>ℹ️ Details</Link>
                </div>

                {/* Report */}
                <div style={{ padding: '10px 14px' }}>
                  {reportingId === c.id ? (
                    <>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>Report status:</div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {['working','busy','broken'].map(s => (
                          <button key={s} onClick={() => setReportStatus(s)} style={{
                            flex: 1, padding: '6px 0', borderRadius: 7, border: `1px solid ${STATUS_COLORS[s]}55`,
                            background: reportStatus === s ? STATUS_COLORS[s] + '22' : 'transparent',
                            color: STATUS_COLORS[s], fontSize: 10, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize',
                          }}>{s}</button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => submitReport(c.id)} disabled={!reportStatus || submitting} style={{
                          flex: 1, padding: '7px 0', borderRadius: 7, border: 'none',
                          background: 'var(--teal)', color: 'var(--navy)', fontWeight: 700, fontSize: 11, cursor: 'pointer', opacity: reportStatus ? 1 : 0.4,
                        }}>{submitting ? '…' : 'Submit'}</button>
                        <button onClick={() => { setReportingId(null); setReportStatus(''); }} style={{
                          padding: '7px 12px', borderRadius: 7, border: '1px solid var(--border-2)',
                          background: 'none', color: 'var(--text-3)', fontSize: 11, cursor: 'pointer',
                        }}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <button onClick={() => { setReportingId(c.id); setReportStatus(''); }} style={{
                      width: '100%', padding: '7px 0', borderRadius: 7, border: '1px solid var(--border-2)',
                      background: 'none', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}>📋 Report Status</button>
                  )}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
